import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  OrientaionPositionCircleBar,
  StepBarVariants,
  StepsLabelVisibilities,
  StepsSchemeDefinition,
  StepsSchemeName
} from "./constants/steps-types";
import {
  STEP_BAR_CIRCLE,
  STEP_BAR_HORIZONTAL,
  STEP_BAR_PICTOGRAM,
  STEP_BAR_VERTICAL,
  STEP_BAR_VERTICAL_ON_MOBILE,
  STEPS_LABEL_VISIBLITY_ACTIVE,
  STEPS_LABEL_VISIBLITY_ALL,
  STEPS_LABEL_VISIBLITY_NONE,
  STEPS_SCHEME_CONFIG,
  STEPS_SCHEME_MAP
} from "./constants/steps-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { StepsSchemeConfiguration } from "../../constants/colors";
import { BackgroundColor } from "../background/constants/background-types";
// import { InitLogging } from "../../decorators/init-logging";
import { isDesktop, isDesktopLarge, isMobile, isTablet } from "../../utils/screen-helper";
import { STEPSIZE_MEDIUM } from "./step/constants/step-constants";
import { scrollToElement } from "../../utils/scroll-helper";
import { StepSize } from "./step/constants/step-types";
import { StyledComponent } from "../../decorators/styled-component";

@Component({
  tag: "eon-ui-steps",
  styleUrl: "steps.scss",
  shadow: true
})
export class Steps implements ColorableComponent<StepsSchemeConfiguration<StepsSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: StepsSchemeConfiguration<StepsSchemeDefinition> = STEPS_SCHEME_CONFIG;
  schemeMap = STEPS_SCHEME_MAP;
  stepsContainer: HTMLElement;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: StepsSchemeDefinition;

  @State() stepElements: Array<HTMLEonUiStepElement>;

  @State() isDesktop: boolean = false;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiStepsElement;

  /**
   * Fires when the selection was clicked
   */
  @Event() valueChanged: EventEmitter<number>;

  /**
   * Raised when the component has been loaded.
   */
  @Event() stepsLoad: EventEmitter<string>;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ mutable: true, reflect: true }) scheme: StepsSchemeName;

  /**
   * Control which step is active
   */
  @Prop({ mutable: true }) activeStep: number;

  /**
   * number of which step is visited but not done/completed
   */
  @Prop() incompleteStep: number;

  /**
   * Set vertical, vertical view on mobile and horizontal view
   */
  @Prop({ mutable: true, reflect: true }) orientation: OrientaionPositionCircleBar = STEP_BAR_HORIZONTAL;

  /**
   * To set the visibility of tooltip for active step
   */
  @Prop({ mutable: true, reflect: true }) tooltipOnActiveStep?: boolean;

  /**
   * To set the visibility of label for active step
   */
  @Prop({ mutable: true }) labelVisibility: StepsLabelVisibilities = STEPS_LABEL_VISIBLITY_ALL;

  /**
   * To set the error on active step
   */
  @Prop({ mutable: true, reflect: true }) error?: boolean;

  /**
   * Set variant of Step Bar
   */
  @Prop({ mutable: true, reflect: true }) variant: StepBarVariants = STEP_BAR_PICTOGRAM;

  /**
   * The size of the steps, if not provided the size is handled automatically whether the step is active or not
   */
  @Prop({ mutable: true }) size: StepSize = STEPSIZE_MEDIUM;

  /**
   * If true, icons or pictograms are going to be replaced by numbers.
   */
  @Prop({ mutable: true }) numberedSteps: boolean;

  /**
   * If true, done steps will be indicated by a checkmark.
   */
  @Prop({ mutable: true }) checkedDoneSteps: boolean;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  componentWillLoad() {
    this.deprecatedFallback();
    this.stepElements = Array.from(this.hostElement.children as HTMLCollectionOf<HTMLEonUiStepElement>);
    this.stepElements[0]?.setAttribute("first-item", "true");
    this.stepElements[this.stepElements.length - 1]?.setAttribute("last-item", "true");
    this.applyScheme();
    this.setStepBarVariant();
    this.setStepNumbered();
    this.setCheckedDoneStep();
    this.setStepLabelVisibilities();
    this.setSizeOfStepBar();
    this.setActiveStepTooltip();
    this.setVerticalToStepsCircleBar();
    this.setErrorState();
    this.addListener();
    this.handleActiveLogic();
    this.handleResize();
  }

  componentDidLoad() {
    this.handleResize();
    this.setCssVariables();
    this.stepsLoad.emit();
  }

  disconnectedCallback() {
    this.stepElements?.forEach((stepElement: HTMLEonUiStepElement) => {
      const circle = stepElement?.shadowRoot?.children[0]?.children[1] as HTMLElement;
      if (!!circle?.onclick) {
        circle.onclick = null;
      }
    });
  }

  deprecatedFallback() {
    const isVerticalView = this.hostElement.getAttribute("vertical-view") === "true";
    if (isVerticalView) {
      this.orientation = STEP_BAR_VERTICAL;
    }
  }

  @Watch("error")
  setErrorState() {
    this.stepElements.forEach((elm) => {
      if (this.error) {
        elm.setAttribute("error", "true");
      } else {
        elm.setAttribute("error", "false");
      }
    });
  }

  @Watch("size")
  setSizeOfStepBar() {
    this.stepElements.forEach((elm) => {
      if (this.size) {
        elm.setAttribute("size", this.size);
      }
    });
  }

  @Watch("labelVisibility")
  setStepLabelVisibilities() {
    switch (this.labelVisibility) {
      case STEPS_LABEL_VISIBLITY_NONE:
        this.stepElements.forEach((step: HTMLEonUiStepElement) => {
          step.hideLabel = true;
        });
        break;
      case STEPS_LABEL_VISIBLITY_ACTIVE:
        this.stepElements.forEach((step: HTMLEonUiStepElement, index: number) => {
          if (index + 1 === this.activeStep) {
            step.hideLabel = false;
          } else {
            step.hideLabel = true;
          }
        });
        break;
      case STEPS_LABEL_VISIBLITY_ALL:
        this.stepElements.forEach((step: HTMLEonUiStepElement) => {
          step.hideLabel = false;
        });
        break;
      default:
        this.stepElements.forEach((step: HTMLEonUiStepElement) => {
          step.hideLabel = false;
        });
        break;
    }
  }

  @Watch("tooltipOnActiveStep")
  setActiveStepTooltip() {
    this.stepElements.forEach((elm) => {
      if (this.tooltipOnActiveStep) {
        elm.setAttribute("tooltip-on-active-step", "true");
      } else {
        elm.setAttribute("tooltip-on-active-step", "false");
      }
    });
  }

  @Watch("orientation")
  setVerticalToStepsCircleBar() {
    this.stepElements.forEach((elm) => {
      if (
        (this.orientation === STEP_BAR_VERTICAL_ON_MOBILE && !this.isDesktop) ||
        this.orientation === STEP_BAR_VERTICAL
      ) {
        elm.setAttribute("is-vertical", "true");
      } else {
        elm.setAttribute("is-vertical", "false");
      }
    });
  }

  @Watch("numberedSteps")
  setStepNumbered() {
    this.stepElements.forEach((step: HTMLEonUiStepElement) => {
      step.numbered = this.numberedSteps;
    });
  }

  @Watch("checkedDoneSteps")
  setCheckedDoneStep() {
    this.stepElements.forEach((step: HTMLEonUiStepElement) => {
      step.checkmarkWhenDone = this.checkedDoneSteps;
    });
  }

  @Watch("variant")
  setStepBarVariant() {
    this.stepElements.forEach((step: HTMLEonUiStepElement) => {
      if (this.variant) {
        step.variant = this.variant;
      }
    });
  }

  @Watch("incompleteStep")
  @Watch("activeStep")
  handleActiveLogic() {
    this.stepElements.forEach((stepElement: HTMLEonUiStepElement, index) => {
      if (this.activeStep - 1 > index || (this.activeStep - 1 < index && index < this.incompleteStep - 1)) {
        stepElement.setAttribute("state", "done");
      }

      if (this.activeStep - 1 === index) {
        stepElement.setAttribute("state", "active");
      }

      if (this.activeStep - 1 < index && !(this.activeStep - 1 < index && index < this.incompleteStep - 1)) {
        stepElement.setAttribute("state", "inactive");
      }

      if (
        this.variant !== STEP_BAR_PICTOGRAM &&
        this.incompleteStep > this.activeStep &&
        this.incompleteStep === index + 1
      ) {
        stepElement.setAttribute("state", "incomplete");
      }
    });
  }

  @Watch("scheme")
  applyScheme() {
    this.stepElements.forEach((stepElement: HTMLEonUiStepElement) => {
      if (this.scheme) {
        stepElement.setAttribute("scheme", this.scheme);
      }
    });

    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    this.stepElements.forEach((stepElement: HTMLEonUiStepElement, index: number) => {
      stepElement.setAttribute("step-index", `${index}`);
    });

    setCssVariablesDelegate(this);
  }

  @Listen("resize", { target: "window" })
  @Watch("activeStep")
  @Watch("orientation")
  @Watch("incompleteStep")
  @Watch("error")
  handleResize() {
    if (
      [STEP_BAR_VERTICAL_ON_MOBILE, STEP_BAR_VERTICAL].includes(this.orientation) &&
      this.variant === STEP_BAR_CIRCLE
    ) {
      const isDesktopView = isDesktop() || isDesktopLarge();
      this.updateDeviceType();
      this.isDesktop = isDesktopView;
      if (this.stepElements?.length > 0) {
        this.stepElements?.forEach((stepElement: HTMLEonUiStepElement) => {
          const classList = stepElement?.shadowRoot?.children[0]?.classList;
          classList?.toggle(
            "eonui-vertical",
            (this.orientation === STEP_BAR_VERTICAL_ON_MOBILE && !this.isDesktop) ||
              this.orientation === STEP_BAR_VERTICAL
          );
        });
      }
    }
  }

  addListener() {
    setTimeout(() => {
      this.stepElements?.forEach((stepElement: HTMLEonUiStepElement) => {
        const isSmallDevice = isMobile() || isTablet();
        const classList = stepElement?.shadowRoot?.children[0]?.classList;
        const circle = stepElement?.shadowRoot?.children[0]?.children[1] as HTMLElement;
        if (!!circle?.onclick) {
          circle.onclick = null;
        }

        if (!this.orientation && isSmallDevice && classList?.contains("eonui-active")) {
          scrollToElement(stepElement, {
            scrollContainer: this.stepsContainer,
            targetPosition: "center",
            offset: isMobile() && (this.stepsContainer.offsetWidth * 30) / 100,
            direction: "horizontally",
            firstChild: this.stepElements[0]
          });
        }

        if (
          (!this.error && classList?.contains("eonui-done")) ||
          (!this.error && classList?.contains("eonui-incomplete"))
        ) {
          if (!!!stepElement.getAttribute("href")) {
            circle.onclick = () => this.valueChanged.emit(stepElement.stepIndex + 1);
          }
        }
      });
    }, 100);
  }

  private updateDeviceType() {
    this.isDesktop = isDesktop() || isDesktopLarge();
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "step-container-wrapper": true,
            vertical:
              (this.orientation === STEP_BAR_VERTICAL_ON_MOBILE && !this.isDesktop) ||
              this.orientation === STEP_BAR_VERTICAL,
            [`${this.variant}`]: !!this.variant
          })}
          ref={(el) => (this.stepsContainer = el as HTMLElement)}
        >
          <div
            class={pfx({
              "step-container": true,
              vertical:
                (this.orientation === STEP_BAR_VERTICAL_ON_MOBILE && !this.isDesktop) ||
                this.orientation === STEP_BAR_VERTICAL
            })}
          >
            <ul
              class={pfx({
                step: true,
                vertical:
                  (this.orientation === STEP_BAR_VERTICAL_ON_MOBILE && !this.isDesktop) ||
                  this.orientation === STEP_BAR_VERTICAL
              })}
            >
              <slot />
            </ul>
          </div>
        </div>
      </Host>
    );
  }
}
